import { lazy } from 'react';

import { useWidget } from 'state/widget/context';

const AdBlockWidget = lazy(
  () =>
    import(
      /* webpackChunkName: "adblock" */ 'components/widgets/AdBlockWidget'
    ),
);
const ArticleStackWidget = lazy(
  () =>
    import(
      /* webpackChunkName: "article" */ 'components/widgets/ArticleStackWidget'
    ),
);

const BannerWidget = lazy(
  () =>
    import(/* webpackChunkName: "banner" */ 'components/widgets/BannerWidget'),
);

const CarouselWidget = lazy(
  () =>
    import(
      /* webpackChunkName: "carousel" */ 'components/widgets/CarouselWidget'
    ),
);

const CBSNewsWidget = lazy(
  () =>
    import(
      /* webpackChunkName: "cbsnews3x1" */ 'components/widgets/cbs/CBSNewsWidget'
    ),
);

const FloatingCarouselWidget = lazy(
  () =>
    import(
      /* webpackChunkName: "floating-carousel" */ 'components/widgets/FloatingCarouselWidget'
    ),
);

const ForcedPlaybackCarouselWidget = lazy(
  () =>
    import(
      /* webpackChunkName: "forced-carousel" */ 'components/widgets/ForcedPlaybackCarouselWidget'
    ),
);

const FrontOfficeSportsWidget = lazy(
  () =>
    import(
      /* webpackChunkName: "frontoffice4X1" */ 'components/widgets/FrontOfficeSportsWidget'
    ),
);

const IABCarouselWidget = lazy(
  () =>
    import(
      /* webpackChunkName: "iabc" */ 'components/widgets/IABCarouselWidget'
    ),
);

const LandscapeCarouselWidget = lazy(
  () =>
    import(
      /* webpackChunkName: "carousel169" */ 'components/widgets/landscapeCarousel/LandscapeCarouselWidget'
    ),
);

const LandscapeAnimatedCarouselWidget = lazy(
  () =>
    import(
      /* webpackChunkName: "carousel169animated" */ 'components/widgets/landscapeCarousel/LandscapeAnimatedCarouselWidget'
    ),
);

const LandscapeCarouselAutoplayWidget = lazy(
  () =>
    import(
      /* webpackChunkName: "carousel169auto" */ 'components/widgets/landscapeCarousel/LandscapeCarouselAutoplayWidget'
    ),
);

const LandscapeInlineCarouselWidget = lazy(
  () =>
    import(
      /* webpackChunkName: "icarousel169" */ 'components/widgets/landscapeCarousel/LandscapeInlineCarouselWidget'
    ),
);

const LATimesWidget = lazy(
  () =>
    import(
      /* webpackChunkName: "latimes2X1" */ 'components/widgets/LATimesWidget'
    ),
);

const MTVWidget = lazy(
  () =>
    import(/* webpackChunkName: "mtv3x1" */ 'components/widgets/cbs/MTVWidget'),
);
const OneByThreeWidget = lazy(
  () =>
    import(/* webpackChunkName: "1x3" */ 'components/widgets/OneByThreeWidget'),
);

const PopSugarWidget = lazy(
  () =>
    import(
      /* webpackChunkName: "popsugar-widget" */ 'components/widgets/PopSugarWidget'
    ),
);

const SCACarouselWidget = lazy(
  () =>
    import(
      /* webpackChunkName: 'sca-carousel' */ 'components/widgets/sca/SCACarouselWidget'
    ),
);

const SCAInlineCarouselWidget = lazy(
  () =>
    import(
      /* webpackChunkName: 'sca-icarousel' */ 'components/widgets/sca/SCAInlineCarouselWidget'
    ),
);

const SCAThreeByOneWidget = lazy(
  () =>
    import(
      /* webpackChunkName: "sca-3x1" */ 'components/widgets/sca/SCAThreeByOneWidget'
    ),
);

const SimpleThreeByOneWidget = lazy(
  () =>
    import(
      /* webpackChunkName: "3x1simple" */ 'components/widgets/SimpleThreeByOneWidget'
    ),
);

const ThreeByOneStackedWidget = lazy(
  () =>
    import(
      /* webpackChunkName: "3x1stacked" */ 'components/widgets/ThreeByOneStackedWidget'
    ),
);

const ThreeByOneStackedLandscapeWidget = lazy(
  () =>
    import(
      /* webpackChunkName: "3x1stackedland" */ 'components/widgets/ThreeByOneStackedLandscapeWidget'
    ),
);

const ThreeByOneHScrollWidget = lazy(
  () =>
    import(
      /* webpackChunkName: "3x1hscroll" */ 'components/widgets/ThreeByOneHScrollWidget'
    ),
);

const ThreeByOneListWidget = lazy(
  () =>
    import(
      /* webpackChunkName: "3x1list" */ 'components/widgets/ThreeByOneListWidget'
    ),
);

export interface WidgetVariantLoaderProps {}

const WidgetVariantLoader: React.FC<WidgetVariantLoaderProps> = () => {
  const {
    state: { variantId },
  } = useWidget();

  switch (variantId) {
    case 'DV_cko4spbf500020uovi0bu245e':
      return <ThreeByOneStackedWidget />;

    case 'DV_ckvii5hsx0000b1qau4ewlvwy':
      return (
        <ThreeByOneStackedWidget
          numberOfUnits={{ horizontal: 3, vertical: 2 }}
        />
      );

    case 'DV_cl01ggvi20000mpogvak63cs6':
      return (
        <ThreeByOneStackedWidget
          numberOfUnits={{ horizontal: 4, vertical: 3 }}
        />
      );

    case 'DV_ckynft65v0000u8qfziuxdqe7':
      return <ThreeByOneStackedLandscapeWidget />;

    case 'DV_cko4sosvd00010uovnm8jz95a':
      return <CarouselWidget />;

    case 'DV_ckykjxrsd0000ytogtbm8exe4':
      return <ForcedPlaybackCarouselWidget />;

    case 'DV_ckr3u0go30000dip199xf5kd5':
      return <ThreeByOneHScrollWidget />;

    case 'DV_ckz30vcue0000fiqde08a5td0':
      return (
        <ThreeByOneHScrollWidget
          artworkAspectRatio={9 / 16}
          artworkResizeStrategy="fit"
          controlsSize="md"
        />
      );

    case 'DV_cl8bxefcb00030uqf30r1idyy':
      return (
        <ThreeByOneHScrollWidget
          artworkAspectRatio={0.666666667}
          artworkResizeStrategy="fill"
          unitSpacing={12}
        />
      );

    case 'DV_ckrdjkgwl00003fp32uv4259g':
      return <ThreeByOneListWidget />;

    case 'DV_ckrqo2kb20000j3p1jq6ee2hp':
      return <IABCarouselWidget />;

    case 'DV_cku46pgry00007gp1r7gun66p':
      return <BannerWidget />;

    case 'DV_cku479vz600017gp1kowo3e9w':
      return <SimpleThreeByOneWidget />;

    case 'DV_ckyepo0kv0000xkqa68bdy5y9':
      return <OneByThreeWidget />;

    case 'DV_ckyot6vk7000084qfdabcw2k2':
      return <PopSugarWidget />;

    case 'DV_ckz4a2t2z0000a8qdrvof5s7i':
      return <CBSNewsWidget />;

    case 'DV_ckz5q1y9y0000w0qdwm5i3szm':
      return <LandscapeCarouselWidget />;

    case 'DV_cl4327bd10000t7qjo5q4glne':
      return <LandscapeAnimatedCarouselWidget />;

    case 'DV_ckzfzqqsq000087qdkuytmtch':
      return <LandscapeInlineCarouselWidget />;

    case 'DV_cl5si3ass00000vp5mhs81n6r':
      return <LandscapeCarouselAutoplayWidget />;

    case 'DV_ckzq01bv00000d8ogv8u92m7v':
      return <FloatingCarouselWidget />;

    case 'DV_cl00061o50000wuog3a446rbv':
      return <SCACarouselWidget />;

    case 'DV_cl0007dk20001wuogrt97wp9h':
      return <SCAThreeByOneWidget />;

    case 'DV_cl3d7hytx00007aq5n03o7cwk':
      return <SCAInlineCarouselWidget />;

    case 'DV_cl1w8nhac00000xrz2nhhgw1j':
      return <ArticleStackWidget podcastNamePrefix="From: " textWrapsPlayer />;

    case 'DV_cl8btkyjs00020uqf8wxg6sr8':
      return <ArticleStackWidget textWrapsPlayer />;

    case 'DV_cl48k178300000zntjhd7l6py':
      return (
        <ArticleStackWidget podcastNamePrefix="From: " playerPosition="left" />
      );

    case 'DV_cl7f6hnrq00000tqy299011s2':
      return <MTVWidget />;

    case 'DV_cl8j7w2px00000smzognlagbs':
      return <LATimesWidget />;

    case 'DV_cl8all8na00000vqfafq7r919':
      return <FrontOfficeSportsWidget />;

    case 'DV_cl9iqnckn00000qo4qohj1c0h':
      return <AdBlockWidget />;

    default:
      throw new Error(`Unhandled widget variant ${variantId}`);
  }
};

export default WidgetVariantLoader;
